import { AppointmentDate } from 'components/AppointmentDate';
import { AppointmentPayment } from 'components/AppointmentPayment';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { Divider } from 'components/Divider';
import { DocumentTitle } from 'components/DocumentTitle';
import { Heading, Paragraph } from 'components/Typography';
import { ScheduleContextV3, withScheduleMachineStateV3 } from 'domains/signupv3/schedule/context';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentMethod } from '@stripe/stripe-js';
import { formatCurrencyDollars } from 'utils/currency';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import { StateAbbrev, toStateFull } from 'utils/states';

const ConfirmEvaluationDate: React.FC = () => {
  const {
    amount: appointmentAmount,
    errorStripeCode,
    errorStripeDeclineCode,
    errorMessage,
    evaluationDate,
    statusCode,
    stripeAccountId,
    therapistName,
  } = ScheduleContextV3.useSelector(({ context }) => context.data);
  const [triedToSubmit, setTriedToSubmit] = useState(false);
  const { state } = ScheduleContextV3.useSelector(({ context }) => context.machineConfig);
  const isScheduling = ScheduleContextV3.useSelector(state => state.matches('schedulingEvaluation'));

  if (!state) {
    throw new Error('State is required');
  }

  const stateName = toStateFull(state as StateAbbrev);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const { send } = ScheduleContextV3.useActorRef();

  const { t } = useTranslation();

  const goBack = useCallback(() => {
    send({
      type: 'back',
    });
  }, [send]);

  const isMobile = useBreakpoint({
    max: 'xs',
  });

  if (!appointmentAmount || !stripeAccountId || !evaluationDate) {
    return null;
  }

  return (
    <>
      <DocumentTitle>{t('schedule.pageTitle')}</DocumentTitle>
      <div className="flex-col mb-40 md:mb-64 items-start">
        <Heading level="h2" className="mb-20 sm-d:mb-12 text-left">
          {t(`schedule.confirm.mainHeading`)}
        </Heading>

        <Paragraph className="mb-40 md:mb-64">{t('schedule.confirm.subTitle')}</Paragraph>

        <Card className="mb-64 bg-orchid-100">
          <Heading level="h6" className="mb-16">
            {t('schedule.confirm.therapistTitle')}
          </Heading>
          <Heading level="h5" className="mt-20 mb-6">
            {therapistName}
          </Heading>
          <Paragraph className="mb-16">M.S., CCC-SLP</Paragraph>
          <Paragraph className="mb-16">
            {t('schedule.confirm.therapistDescription', {
              name: therapistName,
              state: stateName || state || '',
            })}
          </Paragraph>
        </Card>

        <Card>
          <Heading level="h6" className="mb-16">
            {t('schedule.confirm.dateTimeTitle')}
          </Heading>
          <div className="mt-40 mb-16 flex items-center justify-between">
            <Heading level="h5">{t('schedule.confirm.dateTimeSubTitle')}</Heading>
            <Button disabled={isScheduling} size="sm" onClick={goBack} variant="ghost">
              {t('schedule.confirm.edit')}
            </Button>
          </div>

          <AppointmentDate
            date={evaluationDate}
            dateSuffix={t('schedule.confirm.therapistAppointmentComplement', { name: therapistName })}
          />

          <div>
            <Divider className="my-40" />
            <div className="flex items-center justify-between">
              <Heading level="h5" className="font-semibold">
                {t('reservationCard.appointment.amount')}
              </Heading>
              <Heading level="h3">{formatCurrencyDollars(appointmentAmount)}</Heading>
            </div>
          </div>
        </Card>
      </div>

      <AppointmentPayment
        className="mb-64 sm-d:mb-40"
        errorMessage={errorMessage}
        errorStripeCode={errorStripeCode}
        errorStripeDeclineCode={errorStripeDeclineCode}
        onCardChange={() => {
          setTriedToSubmit(false);
          send({ type: 'clearError' });
        }}
        setIsLoading={setIsStripeLoading}
        setPaymentMethod={setPaymentMethod}
        statusCode={statusCode}
        stripeAccountId={stripeAccountId}
        triedToSubmit={triedToSubmit}
      />

      <div className="flex flex-col sm:flex-row w-full xs:w-auto">
        <Button
          data-testid="confirm-reservation-submit-button"
          onClick={() => {
            setTriedToSubmit(true);

            if (!paymentMethod) {
              return;
            }

            send({
              type: 'confirmedEvaluationDate',
              stripePaymentMethodId: paymentMethod.id,
            });
          }}
          size="lg"
          className="mb-[16px] sm:mb-0 sm:mr-8"
          fullWidth={isMobile}
          variant="primary"
          disabled={!paymentMethod || isScheduling || errorMessage}
          loading={isStripeLoading || isScheduling}
        >
          {t('schedule.confirm.submit')}
        </Button>
        <Button disabled={isScheduling} size="lg" onClick={() => send({ type: 'back' })} variant="ghost">
          {t('schedule.confirm.cancel')}
        </Button>
      </div>
    </>
  );
};

export const ScheduleV3ConfirmEvaluationDate = withScheduleMachineStateV3(ConfirmEvaluationDate, [
  'confirmEvaluationDate',
  'schedulingEvaluation',
  'schedulingEvaluationFailed',
]);
